import React, { useState, useEffect } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { Document, Page } from 'react-pdf';
import { read, utils } from 'xlsx';
import { Tabs, Tab } from 'react-bootstrap';

const ViewFileModal = ({ show, handleClose, fileUrl, fileName }) => {

  console.log(fileUrl)
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [excelData, setExcelData] = useState(null);
  const [excelSheets, setExcelSheets] = useState([]);
  const [activeSheet, setActiveSheet] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fileType = fileName?.split('.').pop().toLowerCase();

  useEffect(() => {
    if (show && (fileType === 'xlsx' || fileType === 'csv')) {
      fetchAndParseExcel();
    } else {
      setLoading(false);
    }
  }, [show, fileUrl]);

  const fetchAndParseExcel = async () => {
    try {
      setError(null);
      setLoading(true);

      if (!fileUrl) {
        throw new Error('No file URL provided');
      }

      const response = await fetch(fileUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const blob = await response.blob();
      
      if (blob.size === 0) {
        throw new Error('File is empty');
      }

      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = e.target.result;
          const workbook = read(data, { type: 'array', cellDates: true });
          
          if (!workbook || !workbook.SheetNames || workbook.SheetNames.length === 0) {
            throw new Error('Invalid Excel file format');
          }
          
          // Define allowed sheet names - only show these specific tabs
          const allowedSheetNames = [
            'Products',
            'Marketing',
            'Ingredients',
            'Allergens and Diet',
            'Nutrition',
            'External Info',
            'Hierarchy',
            'Contacts'
          ];

          // Filter to only show allowed sheets that exist in the workbook
          // Use case-insensitive matching to handle potential variations
          const filteredSheetNames = workbook.SheetNames.filter(sheetName =>
            allowedSheetNames.some(allowedName =>
              allowedName.toLowerCase() === sheetName.toLowerCase()
            )
          );

          // Store only filtered sheet names
          setExcelSheets(filteredSheetNames);

          // Parse only filtered sheets into a map of sheet name -> data
          const sheetsData = {};
          filteredSheetNames.forEach(sheetName => {
            const sheet = workbook.Sheets[sheetName];
            sheetsData[sheetName] = utils.sheet_to_json(sheet, {
              header: 1,
              defval: '', // Default value for empty cells
              raw: false  // Convert all data types to strings
            });
          });
          
          setExcelData(sheetsData);
          setActiveSheet(0);
          setLoading(false);
        } catch (error) {
          console.error('Error parsing Excel data:', error);
          setError('Unable to parse Excel file. The file might be corrupted or in an unsupported format.');
          setLoading(false);
        }
      };

      reader.onerror = () => {
        setError('Error reading file');
        setLoading(false);
      };
      
      reader.readAsArrayBuffer(blob);
    } catch (error) {
      console.error('Error fetching or parsing Excel/CSV:', error);
      setError(error.message || 'Error loading file');
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (fileUrl) {
      window.open(fileUrl, '_blank');
    }
  };

  const renderExcelContent = () => {
    if (!excelData || Object.keys(excelData).length === 0) {
      return <div className="alert alert-warning">No data found in file</div>;
    }

    const currentSheetName = excelSheets[activeSheet];
    const currentSheetData = excelData[currentSheetName];

    if (!currentSheetData || currentSheetData.length === 0) {
      return <div className="alert alert-warning">No data found in this sheet</div>;
    }

    return (
      <div style={{ maxHeight: '500px', overflow: 'auto' }}>
        <table className="table table-striped">
          <tbody>
            {currentSheetData.map((row, index) => (
              <tr key={index}>
                {Array.isArray(row) ? row.map((cell, cellIndex) => (
                  <td key={cellIndex}>{cell?.toString() || ''}</td>
                )) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderContent = () => {
    if (loading) {
      return <div className="text-center p-4">Loading...</div>;
    }

    if (error) {
      return (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      );
    }

    switch (fileType) {
      case 'pdf':
        return (
          <div>
            <Document
              file={fileUrl}  // Using the presigned URL for PDF viewing
              onLoadSuccess={({ numPages }) => setNumPages(numPages)}
            >
              <Page pageNumber={pageNumber} />
            </Document>
            <div className="text-center mt-2">
              <button 
                onClick={() => setPageNumber(Math.max(1, pageNumber - 1))}
                disabled={pageNumber <= 1}
              >
                Previous
              </button>
              <span className="mx-2">
                Page {pageNumber} of {numPages}
              </span>
              <button 
                onClick={() => setPageNumber(Math.min(numPages, pageNumber + 1))}
                disabled={pageNumber >= numPages}
              >
                Next
              </button>
            </div>
          </div>
        );

      case 'xlsx':
      case 'csv':
        if (excelSheets.length > 1) {
          return (
            <div>
              <Tabs
                activeKey={activeSheet}
                onSelect={(k) => setActiveSheet(Number(k))}
                className="mb-3"
              >
                {excelSheets.map((sheetName, index) => (
                  <Tab 
                    key={index} 
                    eventKey={index} 
                    title={
                      <div className="d-flex align-items-center">
                        <span>{sheetName}</span>
                      </div>
                    }
                  >
                    {renderExcelContent()}
                  </Tab>
                ))}
              </Tabs>
            </div>
          );
        } else {
          return renderExcelContent();
        }

      case 'png':
      case 'jpg':
      case 'jpeg':
        return (
          <img 
            src={fileUrl}
            alt={fileName} 
            style={{ maxWidth: '100%', maxHeight: '80vh' }} 
          />
        );

      default:
        return <div>Unsupported file type</div>;
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <div>
          <h6 className="mb-1 text-secondary">View Document</h6>
          <Modal.Title>{fileName}</Modal.Title>
        </div>
      </Modal.Header>
      <Modal.Body>
        {renderContent()}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-primary" onClick={handleDownload}>
          Download
        </Button>
        <Button variant="outline-primary" onClick={handleClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ViewFileModal;
